import type { ComponentPropsWithRef, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import type { CalculationData } from '~/api/common/types';

import { Button, Card, Dialog, Illustration, Infotip } from '~/components';
import { useIntl } from '~/hooks/useIntl';
import { clsx } from '~/styles/utils';

import type { VatRegime } from '../documents.utils';

import { AdvantageSimulationDialog } from '../AdvantageSimulationDialog/AdvantageSimulationDialog';
import {
  advantageSimulationButtonStyle,
  copyrightCardStyle,
  copyrightContentStyle,
  copyrightLabelStyle,
  headerStyle,
  illustrationStyle,
  labelStyle,
  rowStyle,
  semiboldStyle,
  totalStyle,
  valueStyle,
  wrapperStyle,
} from './PriceSummary.css';

type Props = ComponentPropsWithRef<'div'> & {
  calculationData: CalculationData;
  vatExemptionReason?: string;
  vatRegime: VatRegime;
  vatTooltip?: ReactNode;
};

export const PriceSummary = ({ calculationData, vatExemptionReason, vatRegime, vatTooltip, ...delegated }: Props) => {
  const { formatCurrency } = useIntl();
  const { t } = useTranslation(['lines']);

  const vatExemptionLabel = match(vatRegime)
    .with('applied', () => null)
    .with('exceptionallyExempt', () => vatExemptionReason ?? t('lines:vatRegime.exceptionallyExempt'))
    .with('export', () => t('lines:vatRegime.export'))
    .with('shifted', () => t('lines:vatRegime.shifted'))
    .exhaustive();

  return (
    <div {...delegated} className={clsx(wrapperStyle, delegated.className)}>
      <div className={headerStyle}>
        {calculationData.coDiscount > 0 && (
          <PriceSummaryRow price={calculationData.coDiscount}>{t('lines:discount.header')}</PriceSummaryRow>
        )}

        <PriceSummaryRow isBold price={calculationData.coExclVat}>
          {t('lines:subtotal')}
        </PriceSummaryRow>

        {vatExemptionLabel !== null ? (
          <PriceSummaryRow>{vatExemptionLabel}</PriceSummaryRow>
        ) : (
          calculationData.coVatAmounts
            .filter(({ amount }) => amount > 0)
            .map(({ amount, percentage }) => (
              <PriceSummaryRow key={percentage} price={amount}>
                {t('lines:vatPercentage', { percentage })}

                {vatTooltip && <Infotip>{vatTooltip}</Infotip>}
              </PriceSummaryRow>
            ))
        )}
      </div>

      <Card.Group>
        <Card.Root size="small" variant="primary-brand">
          <PriceSummaryRow className={totalStyle} isBold price={calculationData.coInclVat}>
            {t('lines:total')}
          </PriceSummaryRow>
        </Card.Root>

        <Card.Root size="small" variant="primary-light">
          <div className={copyrightCardStyle}>
            <Illustration className={illustrationStyle} name="Copyright" strokeWidth={1.4} />

            <div className={copyrightContentStyle}>
              <span className={copyrightLabelStyle}>{t('lines:advantage.label')}</span>

              <Dialog.Root>
                <Dialog.Trigger
                  render={
                    <Button className={advantageSimulationButtonStyle} emphasis="tertiary" icon="ArrowRight" iconPlacement="right">
                      {t('lines:advantage.buttonLabel')}
                    </Button>
                  }
                />

                <AdvantageSimulationDialog calculationData={calculationData} />
              </Dialog.Root>
            </div>

            <span className={clsx(valueStyle, semiboldStyle)}>+ {formatCurrency(calculationData.advantageWithCs?.advantage ?? 0)}</span>
          </div>
        </Card.Root>
      </Card.Group>
    </div>
  );
};

type PriceSummaryRowProps = ComponentPropsWithRef<'div'> & {
  children: ReactNode;
  isBold?: boolean;
  price?: number;
};

const PriceSummaryRow = ({ children, isBold, price, ...delegated }: PriceSummaryRowProps) => {
  const { formatCurrency } = useIntl();

  return (
    <div {...delegated} className={clsx(rowStyle, isBold && semiboldStyle, delegated.className)}>
      <span className={labelStyle}>{children}</span>

      {price !== undefined && <span className={valueStyle}>{formatCurrency(price)}</span>}
    </div>
  );
};
